import { autoserialize, autoserializeAs } from 'cerialize';

import { Address, IAddress } from './address';
import { BaseDTO, IBaseDTO } from './base-dto';
import { CharityMeta, ICharityMeta } from './charity-meta';
import { Contact, IContact } from './contact';
import { CsmContact } from './csm-contact';
import { IMarket, Market } from './market';
import { IOrganization, Organization } from './organization';
import { Screening } from './screening';
import { Sponsorship } from './sponsorship';
import { CharityStore, ICharityStore } from './store';
import { TaxReceiptSettings } from './tax-receipt-settings';

export class Charity extends BaseDTO implements ICharity {
  @autoserialize id?: string;
  @autoserializeAs('created_at') createdAt?: string | null;
  @autoserializeAs('updated_at') updatedAt?: string | null;
  @autoserializeAs('discarded_at') discardedAt?: string | null;
  @autoserializeAs(Market, 'market') market?: Market | null;
  @autoserializeAs('market_id') marketId?: string | null;
  @autoserialize override name: string;
  @autoserialize ein: string;
  @autoserialize score?: number;
  @autoserialize logo?: string | null;
  @autoserialize references: any;
  @autoserializeAs(Address) address: Address;
  @autoserializeAs(Contact, 'primary_contact') primaryContact?: Contact | null;
  @autoserializeAs(Contact, 'website_contact') websiteContact?: Contact | null;
  @autoserializeAs(Contact, 'driver_contact') driverContact?: Contact | null;
  @autoserializeAs(Contact, 'social_media_contact') socialMediaContact?: Contact | null;
  @autoserialize stores?: Array<CharityStore> | null;
  @autoserializeAs(TaxReceiptSettings, 'tax_receipt') taxReceipt?: TaxReceiptSettings | null;
  @autoserialize meta?: CharityMeta | null;
  @autoserialize screening?: Screening | null;
  @autoserializeAs('blacklisted_partners') blacklistedPartners: string[];
  @autoserialize discount?: boolean | null;
  @autoserializeAs(Sponsorship, 'sponsorship') sponsorship?: Sponsorship | null;
  @autoserialize courier?: boolean | null;
  @autoserialize standard?: boolean | null;
  @autoserialize standard_access?: boolean | null;
  @autoserialize kind?: CharityKind;
  @autoserialize standard_preferences?: IStandardPreferences | null;
  @autoserialize items?: boolean | null;
  @autoserialize courier_donor_percentage?: number | null;
  @autoserialize restricted_item_ids?: string[] | null;
  @autoserialize organization_id?: string | null;
  @autoserialize courier_terms?: boolean | null;
  @autoserialize uber_banner?: boolean | null;
  @autoserialize csm_contact?: CsmContact | null;
  @autoserialize csm_photo_url?: string | null;
  @autoserialize csm_calendar_url?: string | null;
  @autoserialize organization?: Organization | null;
  @autoserialize internal_notes?: string | null;
  @autoserialize donation_items?: boolean | null;

  public get zip(): string | undefined {
    return this.address?.zip;
  }

  public get state(): string | undefined {
    return this.address?.state;
  }

  public get phone(): string {
    return this.primaryContact?.phone || '';
  }

  public get fullAddress(): string {
    return this.address.fullAddress;
  }

  constructor(payload?: ICharity) {
    super(payload);
    this.id = payload?.id?.toString();
    this.createdAt = payload?.createdAt;
    this.updatedAt = payload?.updatedAt;
    this.discardedAt = payload?.discardedAt;
    this.market = payload?.market ? new Market(payload.market) : undefined;
    this.marketId = payload?.marketId?.toString() || this.market?.id;
    this.references = payload?.references;
    this.blacklistedPartners = payload?.blacklistedPartners?.map((b) => b.toString()) || [];
    this.address = new Address(payload?.address);
    this.primaryContact = payload?.primaryContact ? new Contact(payload?.primaryContact) : undefined;
    this.websiteContact = payload?.websiteContact ? new Contact(payload?.websiteContact) : undefined;
    this.driverContact = payload?.driverContact ? new Contact(payload?.driverContact) : undefined;
    this.socialMediaContact = payload?.socialMediaContact ? new Contact(payload?.socialMediaContact) : undefined;
    this.taxReceipt = new TaxReceiptSettings(payload?.taxReceipt);
    this.meta = new CharityMeta(payload?.meta);
    this.screening = payload?.screening ? new Screening(payload.screening) : undefined;
    this.name = payload?.name || '';
    this.ein = payload?.ein || '';
    this.score = payload?.score === undefined ? undefined : payload.score;
    this.logo = payload?.logo;
    this.stores = payload?.stores?.map((s) => new CharityStore(s));
    this.discount = payload?.discount;
    this.sponsorship = payload?.sponsorship ? new Sponsorship(payload.sponsorship) : undefined;
    this.courier = payload?.courier;
    this.items = payload?.items;
    this.courier_donor_percentage = payload?.courier_donor_percentage;
    this.restricted_item_ids = payload?.restricted_item_ids?.map((ri) => ri.toString());
    this.organization_id = payload?.organization_id || payload?.organization?.id;
    this.courier_terms = payload?.courier_terms;
    this.uber_banner = payload?.uber_banner;
    this.csm_contact = payload?.csm_contact ? new CsmContact(payload.csm_contact) : undefined;
    this.csm_photo_url = payload?.csm_photo_url;
    this.csm_calendar_url = payload?.csm_calendar_url;
    this.uber_banner = payload?.uber_banner;
    this.organization = payload?.organization ? new Organization(payload.organization) : undefined;
    this.internal_notes = payload?.internal_notes;
    this.standard = payload?.standard;
    this.standard_access = payload?.standard_access;
    this.kind = payload?.kind;
    this.standard_preferences = payload?.standard_preferences;
    this.donation_items = payload?.donation_items;
  }
}

export interface ICharity extends IBaseDTO {
  id?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  discardedAt?: string | null;
  name?: string | null;
  ein?: string | null;
  score?: number;
  logo?: string | null;
  references?: any | null;
  address: IAddress;
  primaryContact?: IContact | null;
  websiteContact?: IContact | null;
  driverContact?: IContact | null;
  socialMediaContact?: IContact | null;
  taxReceipt?: TaxReceiptSettings | null;
  meta?: ICharityMeta | null;
  market?: IMarket | null;
  marketId?: string | null;
  blacklistedPartners?: string[] | null;
  stores?: Array<ICharityStore> | null;
  screening?: Screening | null;
  discount?: boolean | null;
  sponsorship?: Sponsorship | null;
  courier?: boolean | null;
  standard?: boolean | null;
  standard_access?: boolean | null;
  kind?: CharityKind;
  standard_preferences?: IStandardPreferences | null;
  items?: boolean | null;
  courier_donor_percentage?: number | null;
  restricted_item_ids?: string[] | null;
  organization_id?: string | null;
  organization?: IOrganization | null;
  courier_terms?: boolean | null;
  uber_banner?: boolean | null;
  csm_contact?: CsmContact | null;
  csm_photo_url?: string | null;
  csm_calendar_url?: string | null;
  internal_notes?: string | null;
  donation_items?: boolean | null;
}

export type CharityKind = 'priority' | 'standard' | null;

export interface IStandardPreferences {
  disassembly: boolean;
  elevator: boolean;
  lead_time: number;
  photos_required: boolean;
  review_time: number;
  staircases: boolean;
}

export const DEFAULT_STANDARD_PREFERENCES: IStandardPreferences = {
  disassembly: false,
  elevator: false,
  lead_time: 72,
  photos_required: false,
  review_time: 48,
  staircases: false,
};
